<template>
    <div
        class="group relative block h-full overflow-hidden rounded-12 bg-[#FFFFFF0D] p-3 transition-colors duration-500 md:hover:bg-white/15">
        <NuxtLink
            v-if="config.public.website !== 'radioguide.rent'"
            :id="portfolio.slug"
            :to="'/portfolio/' + portfolio.slug"
            :aria-label="`Открыть проект ${portfolio.name}`"
            class="absolute inset-0 z-10 size-full" />
        <div
            class="relative h-[9rem] w-full overflow-hidden rounded-12 md:h-[14.75rem]">
            <picture>
                <source
                    v-if="portfolio.image_main?.[0]?.webp_url"
                    type="image/webp"
                    :srcset="
                        getMediaLink(portfolio.image_main[0].webp_url, 600) ||
                        ''
                    " />

                <source
                    v-else-if="
                        !portfolio.image_main?.[0]?.webp_url &&
                        !portfolio.image_main?.[0]?.url
                    "
                    type="image/webp"
                    srcset="/assets/images/ui/placeholder/webp/cromi.webp" />

                <img
                    loading="lazy"
                    :width="portfolio.image_main?.[0]?.width || 416"
                    :height="portfolio.image_main?.[0]?.height || 234"
                    :alt="portfolio.image_main?.[0]?.alt || portfolio.name"
                    :src="
                        getMediaLink(
                            portfolio.image_main?.[0]?.url || '',
                            600
                        ) || '/assets/images/ui/placeholder/jpg-png/cromi.png'
                    "
                    class="size-full rounded-12 object-cover transition-all duration-700 ease-[cubic-bezier(0.61,-0.53,0.57,1.09)] md:group-hover:-translate-x-[200%] md:group-hover:opacity-0" />
            </picture>

            <picture
                class="absolute inset-0 size-full translate-x-[200%] rounded-12 object-cover opacity-0 transition-all duration-700 ease-[cubic-bezier(0.61,-0.53,0.57,1.09)] md:group-hover:translate-x-0 md:group-hover:opacity-100">
                <source
                    v-if="portfolio.image_optional?.[0]?.webp_url"
                    type="image/webp"
                    :srcset="
                        getMediaLink(
                            portfolio.image_optional[0].webp_url,
                            600
                        ) || ''
                    " />

                <source
                    v-else-if="
                        !portfolio.image_optional?.[0]?.webp_url &&
                        !portfolio.image_optional?.[0]?.url
                    "
                    type="image/webp"
                    srcset="/assets/images/ui/placeholder/webp/cromi.webp" />

                <img
                    loading="lazy"
                    width="416"
                    height="236"
                    :alt="portfolio.image_optional?.[0]?.alt || portfolio.name"
                    :src="
                        getMediaLink(
                            portfolio.image_optional?.[0]?.url || '',
                            600
                        ) || '/assets/images/ui/placeholder/jpg-png/cromi.png'
                    "
                    class="absolute inset-0 size-full translate-x-[200%] rounded-12 object-cover opacity-0 transition-all duration-700 ease-[cubic-bezier(0.61,-0.53,0.57,1.09)] md:group-hover:translate-x-0 md:group-hover:opacity-100" />
            </picture>
        </div>
        <div
            class="mt-5 flex space-x-3 text-16/150 font-medium text-accent-100">
            <span v-if="cityName"> {{ cityName }} </span>
            <time
                v-if="portfolio.started_at"
                :datetime="portfolio.started_at">
                {{ date }}
            </time>
        </div>
        <div
            :class="{
                'md:group-hover:-translate-x-[calc(50%_+_0.75rem)]':
                    hasProjectTools,
            }"
            class="relative z-20 mt-2 grid-cols-2 gap-6 transition-transform duration-700 ease-[cubic-bezier(0.61,-0.53,0.57,1.09)] sm:grid sm:w-[200%] md:mt-5">
            <div>
                <h3
                    class="line-clamp-3 text-20/120 font-semibold text-ash-200 md:text-24/133">
                    <span
                        v-if="isRadioguide()"
                        class="before:absolute before:left-0 before:top-0 before:z-10 before:size-full">
                        {{ portfolio.name }}
                    </span>
                    <NuxtLink
                        v-else
                        :to="'/portfolio/' + portfolio.slug"
                        :aria-label="`Открыть проект ${portfolio.name}`"
                        class="before:absolute before:left-0 before:top-0 before:z-10 before:size-full">
                        {{ portfolio.name }}
                    </NuxtLink>
                </h3>
                <div
                    v-if="portfolio.description_preview"
                    v-html="portfolio.description_preview"
                    class="tinyeditor mt-2 line-clamp-6 text-16/150 font-medium text-accent-100 max-xl:line-clamp-4 max-sm:hidden md:mt-5" />
            </div>
            <div
                class="max-xl:max-h-[250px] max-xl:overflow-y-auto max-sm:hidden">
                <div
                    v-if="categories.length || products.length"
                    class="text-20/120 font-semibold text-ash-200">
                    В этом проекте использовались
                </div>
                <template v-if="config.public.website !== 'radioguide.rent'">
                    <div
                        v-if="categories.length"
                        class="mt-5 flex flex-wrap gap-1 text-16/120 text-ash-200">
                        <NuxtLink
                            v-for="category in categories"
                            :key="category.slug"
                            :to="`/catalog/#${getCategorySlug(category)}`"
                            target="_blank"
                            :aria-label="`Открыть категорию ${category.portfolio_title || category.name}`"
                            class="relative z-20 rounded-full bg-portfolio-category p-2.5"
                            @click.stop>
                            {{ category.portfolio_title || category.name }}
                        </NuxtLink>
                    </div>
                    <!--              TODO: ПОФИКСИТЬ НАЗВАНИЕ НА БЕКЕ!!!! НЕ НУЖНОЕ ДУБЛИРОВАНИЕ КОДА НИЖЕ!!! НУЖНО СМЕНИТЬ НАЗВАНИЕ category на categories -->
                    <div
                        v-else-if="fallbackCategories.length"
                        class="mt-5 flex flex-wrap gap-1 text-16/120 text-ash-200">
                        <NuxtLink
                            v-for="category in fallbackCategories"
                            :key="category.slug"
                            :to="`${isRadioguide() ? '' : '/catalog'}/#${getCategorySlug(category)}`"
                            target="_blank"
                            :aria-label="`Открыть категорию ${category.portfolio_title || category.name}`"
                            class="relative z-20 rounded-full bg-portfolio-category p-2.5"
                            @click.stop>
                            {{ category.portfolio_title || category.name }}
                        </NuxtLink>
                    </div>
                </template>

                <div class="relative z-20 mt-5 flex flex-wrap gap-2 pb-3">
                    <BaseTooltip
                        v-for="product in products.slice(0, 5)"
                        :key="product.id || product.name"
                        :text="product.name">
                        <NuxtLink
                            :to="`${isRadioguide() ? '' : '/catalog'}/${product.slug}`"
                            target="_blank"
                            :aria-label="`Купить ${product.name}`"
                            class="block overflow-hidden rounded-12 shadow-portfolio-product"
                            @click.stop>
                            <picture>
                                <source
                                    v-if="product.images?.[0]?.webp_url"
                                    type="image/webp"
                                    :srcset="
                                        getMediaLink(
                                            product.images[0].webp_url,
                                            200
                                        ) || ''
                                    " />
                                <source
                                    v-else-if="
                                        !product.images?.[0]?.webp_url &&
                                        !product.images?.[0]?.url
                                    "
                                    type="image/webp"
                                    srcset="
                                        /assets/images/ui/placeholder/webp/cromi.webp
                                    " />
                                <img
                                    loading="lazy"
                                    :width="product.images?.[0]?.width || 416"
                                    :height="product.images?.[0]?.height || 234"
                                    :alt="
                                        product.images?.[0]?.alt || product.name
                                    "
                                    :src="
                                        getMediaLink(
                                            product.images?.[0]?.url,
                                            200
                                        ) ||
                                        '/assets/images/ui/placeholder/jpg-png/cromi.png'
                                    "
                                    class="size-[4.375rem] rounded-12 object-contain duration-200 hover:scale-125" />
                            </picture>
                        </NuxtLink>
                    </BaseTooltip>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Category, City } from '~/types/global.types'

import BaseTooltip from '~/components/base/base-tooltip/BaseTooltip.vue'
import type { IPortfolioItem } from '~/components/particles/portfolio-section/portfolio-section.types'
import type { IProductCard } from '~/components/particles/product-card/product-card.types'

import { formatPortfolioDate } from '~/utils/portfolio-date'

type Relation<T> = { data?: T } | T | null | undefined
type RelationArray<T> = { data?: T[] } | T[] | null | undefined

const config = useRuntimeConfig()

const props = defineProps({
    portfolio: {
        type: Object as PropType<IPortfolioItem>,
        required: true,
    },
})

const city = computed(() =>
    getRelationItem<City>(props.portfolio.city as unknown as Relation<City>)
)

const cityName = computed(() => city.value?.name || '')

const categories = computed(() =>
    getRelationData<Category>(
        props.portfolio.categories as unknown as RelationArray<Category>
    )
)

const fallbackCategories = computed(() =>
    getRelationData<Category>(
        props.portfolio.category as unknown as RelationArray<Category>
    )
)

const products = computed(() =>
    getRelationData<IProductCard>(
        props.portfolio.products as unknown as RelationArray<IProductCard>
    )
)

const hasProjectTools = computed(() => {
    return (
        (categories.value.length &&
            config.public.website !== 'radioguide.rent') ||
        products.value.length
    )
})

const date = computed(() => formatPortfolioDate(props.portfolio.started_at))

function getRelationData<T>(relation: RelationArray<T>): T[] {
    if (Array.isArray(relation)) return relation

    if (
        relation &&
        typeof relation === 'object' &&
        'data' in relation &&
        Array.isArray(relation.data)
    ) {
        return relation.data
    }

    return []
}

function getRelationItem<T>(relation: Relation<T>): T | null {
    if (!relation) return null

    if (typeof relation === 'object' && 'data' in relation) {
        return relation.data || null
    }

    return relation as T
}

function getCategorySlug(category: Category) {
    return (
        getRelationItem<Category>(
            category.parent_category as unknown as Relation<Category>
        )?.slug || category.slug
    )
}
</script>
